/**
 * RoutinesCard — routine-health list for the OEM Home strip (Phase 2).
 *
 * Ports the reference HomeOEM "Routines" card: a list of routines, each with a
 * completion-rate progress bar (blue engagement ramp from the Crop Circle info
 * family) and a percentage. Map-plottable: pressing the header toggles the map
 * to plot routine-bearing locations. Consumes `useHomeCards(range)`.
 */
import React from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { tokens, Skeleton, EmptyState, ErrorState } from '@gripp/shared-ui';
import { DashboardCardShell, ViewAllLink } from './dashboardCardShell';
import { ProgressBar } from './cardPrimitives';
import { useHomeCards } from '../hooks/useHomeCards';
import { HomeCardProps } from '../types';

// Routine completion → blue tier (Crop Circle info family). Darker = healthier.
function engagementColor(rate: number): string {
  if (rate >= 95) return tokens.colors.semantic.infoDark; // #1F5B99
  if (rate >= 80) return tokens.colors.semantic.info; // #4A90E2
  return `${tokens.colors.semantic.info}8C`; // muted info blue (~55%) — needs-attention
}

export const RoutinesCard: React.FC<HomeCardProps> = ({
  range,
  selected,
  onToggleSelect,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading, error } = useHomeCards(range);
  const routines = data?.routines;

  const title = t('home.routines.title', 'Routines');
  const action = (
    <ViewAllLink
      label={t('home.cards.viewAll', 'View all')}
      onPress={() => navigate('/new-routines')}
      testID="routines-card-view-all"
    />
  );

  if (isLoading) {
    return (
      <DashboardCardShell title={title} action={action} testID="routines-card"
        loadingSlot={
          <View style={styles.loading}>
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} height={18} radius={4} />
            ))}
          </View>
        }
      >
        {null}
      </DashboardCardShell>
    );
  }
  if (error) {
    return (
      <DashboardCardShell title={title} action={action} testID="routines-card"
        errorSlot={<ErrorState title={t('home.cards.error', 'Could not load data')} testID="routines-card-error" />}
      >
        {null}
      </DashboardCardShell>
    );
  }
  if (!routines || routines.items.length === 0) {
    return (
      <DashboardCardShell title={title} action={action} testID="routines-card"
        emptySlot={<EmptyState title={t('home.routines.empty', 'No routines')} testID="routines-card-empty" />}
      >
        {null}
      </DashboardCardShell>
    );
  }

  return (
    <DashboardCardShell
      title={title}
      subtitle={String(routines.total)}
      action={action}
      testID="routines-card"
      onHeaderPress={onToggleSelect}
      selected={selected}
      headerPressLabel={t('home.routines.plot', 'Plot routine locations on the map')}
    >
      <ScrollView style={styles.list} showsVerticalScrollIndicator={false}>
        {routines.items.map((r, i) => (
          <Pressable
            key={r.id}
            style={[
              styles.row,
              i < routines.items.length - 1 && styles.rowDivider,
            ]}
            onPress={() => navigate('/new-routines')}
            accessibilityRole="button"
            accessibilityLabel={r.name}
          >
            <Text style={styles.name} numberOfLines={1}>{r.name}</Text>
            <ProgressBar value={r.completionRate} max={100} color={engagementColor(r.completionRate)} />
            <Text style={styles.pct}>{r.completionRate}%</Text>
          </Pressable>
        ))}
      </ScrollView>
    </DashboardCardShell>
  );
};

RoutinesCard.displayName = 'RoutinesCard';

const styles = StyleSheet.create({
  loading: {
    gap: tokens.spacing.sm,
  },
  list: {
    flex: 1,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.sm,
    paddingVertical: tokens.spacing.xs,
  },
  // Hairline divider between rows (design: border-b border-subtle last:border-0).
  rowDivider: {
    borderBottomWidth: 1,
    borderBottomColor: tokens.colors.slate[100],
  },
  name: {
    ...tokens.typography.label.medium,
    fontSize: 13,
    lineHeight: 18,
    color: tokens.colors.neutral[800],
    flex: 1,
  },
  pct: {
    ...tokens.typography.label.medium,
    fontWeight: '600',
    color: tokens.colors.slate[400],
    width: 36,
    textAlign: 'right',
    fontVariant: ['tabular-nums'],
  },
});
